// Algolia API Client - Centralized HTTP client for Algolia microservice API calls
// Using native fetch API

/**
 * Base API client configuration for Algolia service
 */
const ALGOLIA_API_BASE_URL = process.env.VUE_APP_ALGOLIA_API_BASE_URL

/**
 * Basic Authentication credentials for Algolia service
 * These are loaded from environment variables
 */
const ALGOLIA_BASIC_AUTH_USER = process.env.VUE_APP_ALGOLIA_BASIC_AUTH_USER || ''
const ALGOLIA_BASIC_AUTH_PASSWORD = process.env.VUE_APP_ALGOLIA_BASIC_AUTH_PASSWORD || ''

/**
 * Generate Basic Auth header value
 * @returns {string|null} Authorization header value or null if credentials are missing
 */
const getBasicAuthHeader = () => {
  if (!ALGOLIA_BASIC_AUTH_USER || !ALGOLIA_BASIC_AUTH_PASSWORD) {
    return null
  }
  // Encode credentials in base64 for HTTP Basic Authentication
  const credentials = `${ALGOLIA_BASIC_AUTH_USER}:${ALGOLIA_BASIC_AUTH_PASSWORD}`
  const encodedCredentials = btoa(credentials)
  return `Basic ${encodedCredentials}`
}

/**
 * Default timeout for requests (in milliseconds)
 */
const DEFAULT_TIMEOUT = 30000 // 30 seconds

/**
 * Make HTTP request with fetch
 * @param {string} url - Full URL or path
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Object>} Response data
 */
const request = async (url, options = {}) => {
  const fullUrl = url.startsWith('http') ? url : `${ALGOLIA_API_BASE_URL}${url}`

  // Get Basic Auth header if credentials are configured
  const authHeader = getBasicAuthHeader()

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      // Add Authorization header if Basic Auth credentials are provided
      ...(authHeader && { Authorization: authHeader }),
      ...options.headers,
    },
    timeout: options.timeout || DEFAULT_TIMEOUT,
    ...options,
  }

  // Remove timeout from fetch options (fetch doesn't support timeout directly)
  delete defaultOptions.timeout

  try {
    // Create abort controller for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(
      () => controller.abort(),
      defaultOptions.timeout || DEFAULT_TIMEOUT
    )

    const response = await fetch(fullUrl, {
      ...defaultOptions,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    // Check if response is ok
    if (!response.ok) {
      const errorText = await response.text()
      let errorData
      try {
        errorData = JSON.parse(errorText)
      } catch {
        errorData = {
          message: errorText || `HTTP Error: ${response.status} ${response.statusText}`,
        }
      }

      throw {
        status: response.status,
        statusText: response.statusText,
        data: errorData,
        message: errorData.message || `HTTP Error: ${response.status} ${response.statusText}`,
      }
    }

    // Parse response
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return await response.json()
    } else {
      return await response.text()
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      throw {
        message: 'La solicitud tardó demasiado tiempo. Por favor, intenta nuevamente.',
        timeout: true,
      }
    }

    if (error.status) {
      // Already processed HTTP error
      throw error
    }

    // Network or other errors
    throw {
      message: error.message || 'Error de conexión. Verifica tu conexión a internet.',
      networkError: true,
      originalError: error,
    }
  }
}

/**
 * GET request
 * @param {string} url - URL or path
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Response data
 */
export const get = async (url, options = {}) => {
  return request(url, {
    method: 'GET',
    ...options,
  })
}

/**
 * POST request
 * @param {string} url - URL or path
 * @param {Object} data - Request body data
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Response data
 */
export const post = async (url, data = {}, options = {}) => {
  return request(url, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options,
  })
}

/**
 * POST request with multipart/form-data body.
 * Do not set Content-Type manually; the browser adds the boundary.
 * @param {string} url - URL or path
 * @param {FormData} formData - FormData payload
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} Response data
 */
export const postFormData = async (url, formData, options = {}) => {
  const fullUrl = url.startsWith('http') ? url : `${ALGOLIA_API_BASE_URL}${url}`
  const authHeader = getBasicAuthHeader()
  const timeout = options.timeout || DEFAULT_TIMEOUT

  const headers = {
    ...(authHeader && { Authorization: authHeader }),
    ...options.headers,
  }

  delete options.timeout
  delete options.headers

  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: formData,
      signal: controller.signal,
      ...options,
    })

    clearTimeout(timeoutId)

    const contentType = response.headers.get('content-type')
    let payload

    if (contentType && contentType.includes('application/json')) {
      payload = await response.json()
    } else {
      payload = await response.text()
    }

    if (!response.ok) {
      const errorData = typeof payload === 'object' ? payload : { message: String(payload) }

      throw {
        status: response.status,
        statusText: response.statusText,
        data: errorData,
        message:
          errorData.message ||
          errorData.data?.message ||
          `HTTP Error: ${response.status} ${response.statusText}`,
      }
    }

    return payload
  } catch (error) {
    if (error.name === 'AbortError') {
      throw {
        message: 'La solicitud tardó demasiado tiempo. Por favor, intenta nuevamente.',
        timeout: true,
      }
    }

    if (error.status) {
      throw error
    }

    throw {
      message: error.message || 'Error de conexión. Verifica tu conexión a internet.',
      networkError: true,
      originalError: error,
    }
  }
}

/**
 * PUT request
 * @param {string} url - URL or path
 * @param {Object} data - Request body data
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Response data
 */
export const put = async (url, data = {}, options = {}) => {
  return request(url, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options,
  })
}

/**
 * DELETE request
 * @param {string} url - URL or path
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Response data
 */
export const del = async (url, options = {}) => {
  return request(url, {
    method: 'DELETE',
    ...options,
  })
}

/**
 * Update stock for a product in Algolia
 * @param {string} objectID - Product code (objectID)
 * @param {number} division - Division number
 * @param {number} pickedQuantity - Quantity picked/sold
 * @param {number} requestedQuantity - Quantity originally requested
 * @param {string} reasonCode - Reason code (NONE, OUT_OF_STOCK, OTHER)
 * @returns {Promise<Object>} Result of stock update
 */
export const updateStockProduct = async (
  objectID,
  division,
  pickedQuantity,
  requestedQuantity,
  reasonCode
) => {
  try {
    if (!objectID) {
      throw { message: 'objectID es requerido' }
    }

    const updates = {
      division: division,
      quantity: pickedQuantity,
    }

    // Si motivo es OUT_OF_STOCK y surtido parcial, incluir binary: 0
    if (reasonCode === 'OUT_OF_STOCK' && pickedQuantity !== requestedQuantity) {
      updates.binary = 0
    }

    const response = await post('/api/v1/products/update-stock-product', {
      objectID: objectID,
      updates: updates,
    })

    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Error in updateStockProduct:', error)
    return {
      success: false,
      error: error.message || 'Error al actualizar stock en Algolia',
      details: error,
    }
  }
}

/**
 * Mark product as unavailable (no stock) in Algolia
 * @param {string} objectID - Product code (objectID)
 * @param {number} division - Division number
 * @returns {Promise<Object>} Result of stock update
 */
export const markProductUnavailable = async (objectID, division) => {
  try {
    if (!objectID) {
      throw { message: 'objectID es requerido' }
    }

    const updates = {
      division: division,
      quantity: 0,
      binary: 0,
    }

    const response = await post('/api/v1/products/update-stock-product', {
      objectID: objectID,
      updates: updates,
    })

    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Error in markProductUnavailable:', error)
    return {
      success: false,
      error: error.message || 'Error al marcar producto como no disponible en Algolia',
      details: error,
    }
  }
}

export default {
  get,
  post,
  postFormData,
  put,
  delete: del,
  request,
  updateStockProduct,
  markProductUnavailable,
}
